import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Divider, PageHeader, Typography, Timeline, Button, Spin } from 'antd';
import '../../../styles/components/MarketPlace/JobApplicationStatus.scss';
import {
  CheckCircleFilled,
  FileTextOutlined,
  ExclamationCircleFilled,
  WarningFilled,
  PhoneOutlined,
} from '@ant-design/icons';
import { getJobApplicationStatusByApplicationId } from '../../../services/API/MarketplaceService';
import { toast } from 'react-toastify';
import { BsClockFill } from 'react-icons/bs';
import '../../../styles/components/MarketPlace/JobApplicationStatus.scss';
import JobDescriptionModal from '../JobDescriptionModal';
import ScreenNavigation from '../ScreenNavigation';
import { useHistory } from 'react-router-dom';
import { getClientIcon } from '../../../utils/MarketplaceUtils';
import { Navigation } from '../../../services/NavigationService';
import { Routes } from '../../../constants/RouterConstants';
import moment from 'moment';
import useMoengage from '../../../hooks/useMoengage';
import { useMitraStore } from '../../../contexts/MitraContext';
import DocumentsErrorDrawer from './DocumentsErrorDrawer';
import JobApplicationStatusContainerSkeleton from '../skeletons/JobApplicationStatusContainerSkeleton';
import ReusableCarousel from '../ReusableCarousel';
import { applicationStatusPageCarousel } from '../../../constants/MarketplaceConstants';
import { useTranslation } from 'react-i18next';
import { MARKETPLACE_CONSTANTS } from '../../../constants/MarketplaceConstants';

const { Text } = Typography;

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function JobApplicationStatusContainer() {
  const history = useHistory();
  const location = useLocation();
  const [timelineData, setTimelineData] = useState([]);
  const [jobDetails, setJobDetails] = useState({});
  const query = useQuery();
  const jobApplicationId = query.get('applicationId');
  const [loading, setLoading] = useState(true);
  const [, trackEvent] = useMoengage();
  const { selectedJobDescription } = useMitraStore();
  const { t } = useTranslation();
  const [showDocumentsErrorDrawer, setShowDocumentsErrorDrawer] = useState(false);
  const [documentUploadErrors, setDocumentUploadErrors] = useState([]);
  const [nativeNavigationUrl, setNativeNavigationUrl] = useState('');
  const { selectedMitraDetails } = useMitraStore();

  const setCurrentMilestone = timeLineDetails => {
    const processedTimelineDetails = [...timeLineDetails];
    let prevMilestoneAchieved = false;
    const totalTimelineItems = processedTimelineDetails.length;

    for (let i = 0; i < totalTimelineItems; i++) {
      // if previous milestone is achieved, then current milestone is set as current objective
      // done to achieve css for current milestone as well as to hide action buttons of other pending milestones
      if (prevMilestoneAchieved) {
        processedTimelineDetails[i].currentObjective = true;
        prevMilestoneAchieved = false; // turning it false as current milestone is detected
      } else {
        processedTimelineDetails[i].currentObjective = false;
      }

      // if current milestone is marked as completed, then turning the 'prevMilestoneAchieved' flag to true
      if (processedTimelineDetails[i].isCompleted) {
        prevMilestoneAchieved = true;
      }
    }
    setTimelineData([...processedTimelineDetails]);
  };

  const [clientIcon, setClientIcon] = useState('');

  useEffect(() => {
    setLoading(true);
    getJobApplicationStatusByApplicationId(jobApplicationId)
      .then(response => {
        setLoading(false);
        if (response.status !== 200) {
          toast.error('Something went wrong, please try again later', {
            position: toast.POSITION.TOP_CENTER,
          });
        } else {
          return response.data;
        }
      })
      .then(responseObj => {
        setTimelineData(responseObj.timelineDetails);
        setCurrentMilestone(responseObj.timelineDetails); // used to indicate currentMilestone
        responseObj.salary = `₹${responseObj.salary.toLocaleString()}`;
        setJobDetails(responseObj);
        setClientIcon(getClientIcon(responseObj.clientName));
      })
      .catch(error => {
        setLoading(false);
        console.log(error);
      });
  }, []);

  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);

  const showModal = () => {
    setShowJobDescriptionModal(true);
  };

  const handleOk = () => {
    setShowJobDescriptionModal(false);
  };

  const handleCancel = () => {
    setShowJobDescriptionModal(false);
  };

  const navigateToUrl = url => {
    history.replace(url);
  };

  const triggerDocumentUploadErrorsDrawer = (errorData, documentPageUrl) => {
    setNativeNavigationUrl(documentPageUrl);
    setDocumentUploadErrors([...errorData]);
    setShowDocumentsErrorDrawer(true);
  };

  const triggerEvent = () => {
    trackEvent('jse_call_us_clicked', {
      userId: localStorage.getItem('userId'),
      userPhoneNumber: selectedMitraDetails?.userPhoneNumber,
      timeStamp: moment().format('MM/DD/YYYY HH:mm:ss'),
    });
  };

  return (
    <>
      {/* <Spin spinning={loading} className='spinner' tip={"Loading"} /> */}
      <JobDescriptionModal
        clientName={jobDetails?.clientName}
        selectedJobDescription={selectedJobDescription}
        showJobDescriptionModal={showJobDescriptionModal}
        handleCancel={handleCancel}
        handleOk={handleOk}
      />
      <DocumentsErrorDrawer
        open={showDocumentsErrorDrawer}
        setShowDocumentsErrorDrawer={setShowDocumentsErrorDrawer}
        errorData={documentUploadErrors}
        nativeNavigationUrl={nativeNavigationUrl}
      />
      <PageHeader
        onBack={() => {
          const route = {
            pathname: Routes.JOB_LISTINGS,
            search: `city=${localStorage.getItem('preferredLocation')}`,
          };
          Navigation.push(route);

          // history.replace(`/job-listings?city=${localStorage.getItem('preferredLocation')}`)
        }}
        className="site-page-header"
        title={t('job_application')}
        extra={[
          <div className="inbound-calling-container">
            <div className="help">Need Help?</div>
            <div className="call-us">
              <a href={`tel:+91-6366783062`}>
                <Button onClick={() => triggerEvent()}>
                  <PhoneOutlined />
                  Call us
                </Button>
              </a>
            </div>
          </div>,
        ]}
      />
      {loading ? <JobApplicationStatusContainerSkeleton /> : null}
      {!loading && Object.keys(jobDetails) && timelineData.length > 0 ? (
        <Fragment>
          <div className="reusable-carousel-div">
            <ReusableCarousel carouselProps={applicationStatusPageCarousel} />
          </div>
          <div className="job-application-main-container">
            {jobDetails ? (
              <div className="job-summary-details-container">
                <div className="salary-details-container">
                  <Text style={{ fontsize: '1.2em' }}>
                    <b>{jobDetails?.baseSalary}</b>/ month
                  </Text>
                  <Text className="view-details" onClick={showModal}>
                    {t('view_details')}
                  </Text>
                </div>
                <Text style={{ fontsize: '1.1em' }}>{jobDetails?.jobType} Executive</Text>
                <div className="client-info-container">
                  <img src={clientIcon} alt={jobDetails?.clientName} style={{ height: '50px' }} />
                  <div className="client-name-city">
                    <Text className="client-name">
                      {jobDetails.displayName
                        ? jobDetails.displayName?.charAt(0).toUpperCase() +
                          jobDetails.displayName?.slice(1)
                        : jobDetails.clientName?.charAt(0).toUpperCase() +
                          jobDetails.clientName?.slice(1)}
                    </Text>
                    <Text className="city-name">{jobDetails?.location}</Text>
                  </div>
                </div>
              </div>
            ) : (
              ''
            )}

            <Divider />

            <div className="job-application-container">
              <Timeline>
                {timelineData.length
                  ? timelineData.map(item => {
                      return (
                        <Timeline.Item
                          key={item.id}
                          dot={
                            item.isCompleted ? (
                              <CheckCircleFilled style={{ color: '#27A745' }} />
                            ) : item.currentObjective ? (
                              <BsClockFill style={{ color: 'orange' }} />
                            ) : (
                              ''
                            )
                          }
                        >
                          <div className="item-container">
                            <Text className="status-header">
                              <Text>{item.header}</Text>
                              {item.status === 'pending' && item.currentObjective ? (
                                <Text className="pending-txt">Pending</Text>
                              ) : (
                                ''
                              )}
                            </Text>
                            <Text className="sub-header">
                              {item.header === 'Application Started' &&
                              moment(item?.subHeader).isValid()
                                ? moment(item?.subHeader).format('dddd, DD MMM YYYY')
                                : item.subHeader}
                              {item.header === 'Documents Uploaded' &&
                              item.documentErrors?.length &&
                              item.documentErrors.length > 0 ? (
                                <Text className="sub-header">
                                  Oops!
                                  {jobDetails.displayName
                                    ? ' ' +
                                      jobDetails.displayName?.charAt(0).toUpperCase() +
                                      jobDetails.displayName?.slice(1) +
                                      ' '
                                    : ' ' +
                                      jobDetails.clientName?.charAt(0).toUpperCase() +
                                      jobDetails.clientName?.slice(1) +
                                      ' '}
                                  team found some issues in the uploaded document.
                                  {/* <a style={{color:'#D34B18', fontWeight:'700'}} onClick={()=> triggerDocumentUploadErrorsDrawer(item.documentErrors)}>Check Here</a> */}
                                </Text>
                              ) : null}
                            </Text>

                            {// if navigation via button is to be done to native app, shows only for ongoing milestone
                            item.currentObjective &&
                            item.button &&
                            item.status === 'pending' &&
                            item.navigationTo !== 'local' ? (
                              <>
                                {item.header === 'Documents Uploaded' &&
                                item.documentErrors?.length ? (
                                  <Button
                                    className="timeline-button"
                                    onClick={() =>
                                      triggerDocumentUploadErrorsDrawer(
                                        item.documentErrors,
                                        item.buttonUrl
                                      )
                                    }
                                  >
                                    <div className="button-content">
                                      <WarningFilled />
                                      <Text>Check Errors</Text>
                                    </div>
                                  </Button>
                                ) : (
                                  <a
                                    className={`hyperlink-anchor`}
                                    href={`${item.buttonUrl}${
                                      parseInt(localStorage.getItem('appVersion')) > 66
                                        ? `&city=${localStorage.getItem(
                                            'preferredLocation'
                                          )}&entryVia=${location.pathname.substring(1)}`
                                        : ''
                                    }`}
                                  >
                                    <Button className="timeline-button">
                                      <div className="button-content">
                                        <FileTextOutlined />
                                        {item.buttonText}
                                      </div>
                                    </Button>
                                  </a>
                                )}
                              </>
                            ) : null}

                            {// if navigation via button is to be done to same pwa, shows only for ongoing milestone
                            item.currentObjective &&
                            item.button &&
                            item.status === 'pending' &&
                            item.navigationTo === 'local' ? (
                              <Button
                                className="timeline-button"
                                onClick={() => navigateToUrl(item.buttonUrl)}
                              >
                                <div className="button-content">
                                  <FileTextOutlined />
                                  {item.buttonText}
                                </div>
                              </Button>
                            ) : null}

                            {// for invite links to client apps, shows if not pending, irrespective of current objective
                            item.button &&
                            item.status === 'notPending' &&
                            item.navigationTo === 'web' ? (
                              <a href={item.buttonUrl} rel="noreferrer" target="_blank">
                                <Button className="timeline-button">
                                  <div className="button-content">
                                    <FileTextOutlined />
                                    {item.buttonText}
                                  </div>
                                </Button>
                              </a>
                            ) : null}
                          </div>
                        </Timeline.Item>
                      );
                    })
                  : ''}
              </Timeline>
            </div>
          </div>
        </Fragment>
      ) : (
        !loading && (
          <div className="no-status-container">
            <img src="/images/no-jobs.png" />
            <Text className="no-jobs-text">
              No information available, <br /> Please check after sometime.
            </Text>
          </div>
        )
      )}

      {MARKETPLACE_CONSTANTS.NAVIGATION_ITEMS.length > 1 ? <ScreenNavigation /> : null}
    </>
  );
}

export default JobApplicationStatusContainer;
